import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

type TutorialGuide = 'addBanknote' | 'editBanknote' | 'suggestPicture';

interface TutorialState {
  currentGuide: TutorialGuide | null;
  currentStep: number;
  totalSteps: number;
  isVisible: boolean;
}

interface TutorialContextType {
  tutorialState: TutorialState;
  showGuide: (guide: TutorialGuide, force?: boolean) => void;
  nextStep: () => void;
  previousStep: () => void;
  hideTutorial: () => void;
  completeTutorial: () => void;
  skipTutorial: () => void;
  resetTutorials: () => void;
  isNewUser: boolean;
  completedGuides: Set<TutorialGuide>;
  triggerEditBanknoteGuide: () => void;
  triggerSuggestPictureGuide: () => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [tutorialState, setTutorialState] = useState<TutorialState>({
    currentGuide: null,
    currentStep: 1,
    totalSteps: 0,
    isVisible: false
  });
  const [completedGuides, setCompletedGuides] = useState<Set<TutorialGuide>>(new Set());
  const [guideShowCounts, setGuideShowCounts] = useState<Record<TutorialGuide, number>>({
    addBanknote: 0,
    editBanknote: 0,
    suggestPicture: 0
  });
  const [isNewUser, setIsNewUser] = useState(false);

  // Enhanced new user detection
  const checkIfNewUser = (user: any) => {
    if (!user?.created_at) return false;
    
    const userCreatedAt = new Date(user.created_at);
    const now = new Date();
    const daysSinceCreation = Math.floor((now.getTime() - userCreatedAt.getTime()) / (1000 * 60 * 60 * 24));
    
    // Consider user "new" if account created within last 14 days
    return daysSinceCreation <= 14;
  };

  // Get total steps for a guide from guide.json structure
  const getGuideSteps = (guide: TutorialGuide): number => {
    // These correspond to the steps in guide.json
    const stepCounts = {
      addBanknote: 6,
      editBanknote: 10,
      suggestPicture: 4
    };
    return stepCounts[guide];
  };

  // Load tutorial state from localStorage
  useEffect(() => {
    if (user) {
      const savedState = localStorage.getItem(`tutorials_${user.id}`);
      const isNew = checkIfNewUser(user);
      setIsNewUser(isNew);
      
      if (savedState) {
        const { completed, showCounts } = JSON.parse(savedState);
        setCompletedGuides(new Set(completed));
        setGuideShowCounts(showCounts || {
          addBanknote: 0,
          editBanknote: 0,
          suggestPicture: 0
        });
      } else if (isNew) {
        // New user - show addBanknote guide (welcome) after 2 seconds
        setTimeout(() => {
          showGuide('addBanknote');
        }, 2000);
      }
    }
  }, [user]);

  // Save tutorial state to localStorage
  const saveTutorialState = (completed: Set<TutorialGuide>, showCounts?: Record<TutorialGuide, number>) => {
    if (user) {
      localStorage.setItem(`tutorials_${user.id}`, JSON.stringify({
        completed: Array.from(completed),
        showCounts: showCounts || guideShowCounts
      }));
    }
  };

  const showGuide = (guide: TutorialGuide, force: boolean = false) => {
    // Don't show if already completed (unless manually triggered or forced)
    if (completedGuides.has(guide) && !force) return;
    
    // Check 3-time limit per guide (unless forced)
    if (!force && guideShowCounts[guide] >= 3) return;
    
    // Increment show count
    const newShowCounts = { ...guideShowCounts, [guide]: guideShowCounts[guide] + 1 };
    setGuideShowCounts(newShowCounts);
    saveTutorialState(completedGuides, newShowCounts);
    
    const totalSteps = getGuideSteps(guide);
    setTutorialState({
      currentGuide: guide,
      currentStep: 1,
      totalSteps,
      isVisible: true
    });
  };

  const nextStep = () => {
    setTutorialState(prev => {
      if (prev.currentStep < prev.totalSteps) {
        return { ...prev, currentStep: prev.currentStep + 1 };
      }
      return prev;
    });
  };

  const previousStep = () => {
    setTutorialState(prev => {
      if (prev.currentStep > 1) {
        return { ...prev, currentStep: prev.currentStep - 1 };
      }
      return prev;
    });
  };

  const hideTutorial = () => {
    setTutorialState(prev => ({ ...prev, isVisible: false }));
    setTimeout(() => {
      setTutorialState(prev => ({ ...prev, currentGuide: null, currentStep: 1, totalSteps: 0 }));
    }, 300);
  };

  const completeTutorial = () => {
    if (tutorialState.currentGuide) {
      const newCompleted = new Set(completedGuides);
      newCompleted.add(tutorialState.currentGuide);
      setCompletedGuides(newCompleted);
      saveTutorialState(newCompleted);
    }
    hideTutorial();
  };

  const skipTutorial = () => {
    hideTutorial();
  };

  const resetTutorials = () => {
    setCompletedGuides(new Set());
    setGuideShowCounts({
      addBanknote: 0,
      editBanknote: 0,
      suggestPicture: 0
    });
    if (user) {
      localStorage.removeItem(`tutorials_${user.id}`);
    }
  };

  // Trigger functions for specific events
  const triggerEditBanknoteGuide = () => {
    if (!completedGuides.has('editBanknote') && guideShowCounts['editBanknote'] < 3 && user) {
      setTimeout(() => showGuide('editBanknote'), 1000);
    }
  };

  const triggerSuggestPictureGuide = () => {
    if (!completedGuides.has('suggestPicture') && guideShowCounts['suggestPicture'] < 3 && user) {
      setTimeout(() => showGuide('suggestPicture'), 1000);
    }
  };

  // Debug functions for testing
  const debugShowGuide = (guide: TutorialGuide) => {
    console.log(`🎯 Debug: Showing guide: ${guide}`);
    showGuide(guide, true); // Force show even if completed
  };

  const debugResetTutorials = () => {
    console.log('🎯 Debug: Resetting all tutorials');
    setCompletedGuides(new Set());
    setGuideShowCounts({
      addBanknote: 0,
      editBanknote: 0,
      suggestPicture: 0
    });
    setIsNewUser(true);
    if (user) {
      localStorage.removeItem(`tutorials_${user.id}`);
    }
  };

  // Comprehensive debug function for testing all tutorials
  const debugTutorials = () => {
    console.log('🎯 Debug: Starting comprehensive tutorial debug mode');
    
    // Reset all tutorial progress
    setCompletedGuides(new Set());
    setGuideShowCounts({
      addBanknote: 0,
      editBanknote: 0,
      suggestPicture: 0
    });
    setIsNewUser(true);
    if (user) {
      localStorage.removeItem(`tutorials_${user.id}`);
    }
    
    // Hide any current tutorial
    setTutorialState({
      currentGuide: null,
      currentStep: 1,
      totalSteps: 0,
      isVisible: false
    });
    
    // Show welcome tutorial after a brief delay
    setTimeout(() => {
      console.log('🎯 Debug: Showing addBanknote (welcome) guide');
      showGuide('addBanknote', true);
    }, 500);
    
    console.log('✅ Debug mode active - all guides can now be triggered even if previously completed');
  };

  // Expose debug functions to window for console access
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).showGuide = debugShowGuide;
      (window as any).resetTutorials = debugResetTutorials;
      (window as any).debugTutorials = debugTutorials;
      (window as any).triggerEditGuide = triggerEditBanknoteGuide;
      (window as any).triggerSuggestGuide = triggerSuggestPictureGuide;
    }
  }, [user]);

  return (
    <TutorialContext.Provider value={{
      tutorialState,
      showGuide,
      nextStep,
      previousStep,
      hideTutorial,
      completeTutorial,
      skipTutorial,
      resetTutorials,
      isNewUser,
      completedGuides,
      triggerEditBanknoteGuide,
      triggerSuggestPictureGuide
    }}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};